import type { ThemeEditorSectionUnloadEvent } from '@/types/shopify'

import BaseSection from '@/sections/base'

const selectors = {
  form: 'form[action*="/password"]',
  maskedInput: '[data-password-mask-display]',
  realInput: '[data-password-mask-real]'
}

export default class PasswordHomeSection extends BaseSection {
  static TYPE = 'main-password-home'

  form: HTMLFormElement | undefined
  maskedInput: HTMLInputElement | undefined
  realInput: HTMLInputElement | undefined
  rawValue: string

  onMaskedKeyDownBound: (e: KeyboardEvent) => void
  onMaskedBeforeInputBound: (e: InputEvent) => void
  onMaskedPasteBound: (e: ClipboardEvent) => void
  onMaskedDropBound: (e: DragEvent) => void
  onMaskedFocusBound: () => void

  constructor(container: HTMLElement) {
    super(container)

    this.form = this.qs(selectors.form) as HTMLFormElement | undefined
    this.maskedInput = this.qs(selectors.maskedInput) as HTMLInputElement | undefined
    this.realInput = this.qs(selectors.realInput) as HTMLInputElement | undefined
    this.rawValue = this.realInput?.value || ''

    this.onMaskedKeyDownBound = this.onMaskedKeyDown.bind(this)
    this.onMaskedBeforeInputBound = this.onMaskedBeforeInput.bind(this)
    this.onMaskedPasteBound = this.onMaskedPaste.bind(this)
    this.onMaskedDropBound = this.onMaskedDrop.bind(this)
    this.onMaskedFocusBound = this.onMaskedFocus.bind(this)

    this.initializeMask()
  }

  onUnload(e: ThemeEditorSectionUnloadEvent) {
    this.maskedInput?.removeEventListener('keydown', this.onMaskedKeyDownBound)
    this.maskedInput?.removeEventListener('beforeinput', this.onMaskedBeforeInputBound as EventListener)
    this.maskedInput?.removeEventListener('paste', this.onMaskedPasteBound)
    this.maskedInput?.removeEventListener('drop', this.onMaskedDropBound)
    this.maskedInput?.removeEventListener('focus', this.onMaskedFocusBound)

    super.onUnload(e)
  }

  initializeMask() {
    if (!this.maskedInput || !this.realInput) return

    this.renderMaskedValue()

    this.maskedInput.addEventListener('keydown', this.onMaskedKeyDownBound)
    this.maskedInput.addEventListener('beforeinput', this.onMaskedBeforeInputBound as EventListener)
    this.maskedInput.addEventListener('paste', this.onMaskedPasteBound)
    this.maskedInput.addEventListener('drop', this.onMaskedDropBound)
    this.maskedInput.addEventListener('focus', this.onMaskedFocusBound)
  }

  renderMaskedValue() {
    if (!this.maskedInput || !this.realInput) return

    this.realInput.value = this.rawValue
    this.maskedInput.value = '*'.repeat(this.rawValue.length)
  }

  appendText(value: string) {
    if (!value) return

    this.rawValue += value
    this.renderMaskedValue()
  }

  removeLastCharacter() {
    if (!this.rawValue.length) return

    this.rawValue = this.rawValue.slice(0, -1)
    this.renderMaskedValue()
  }

  onMaskedKeyDown(e: KeyboardEvent) {
    if (e.key === 'Enter' || e.key === 'Tab') return

    if (e.key === 'Backspace') {
      e.preventDefault()
      this.removeLastCharacter()
      return
    }

    if (e.key === 'Delete') {
      e.preventDefault()
      this.rawValue = ''
      this.renderMaskedValue()
      return
    }

    if (e.ctrlKey || e.metaKey || e.altKey) return

    if (e.key.length === 1) {
      e.preventDefault()
      this.appendText(e.key)
    }
  }

  onMaskedBeforeInput(e: InputEvent) {
    if (e.inputType === 'insertText' && e.data) {
      e.preventDefault()
      this.appendText(e.data)
      return
    }

    if (e.inputType === 'deleteContentBackward') {
      e.preventDefault()
      this.removeLastCharacter()
      return
    }

    if (e.inputType === 'deleteContentForward') {
      e.preventDefault()
      this.rawValue = ''
      this.renderMaskedValue()
    }
  }

  onMaskedPaste(e: ClipboardEvent) {
    e.preventDefault()

    const value = e.clipboardData?.getData('text') || ''

    this.appendText(value)
  }

  onMaskedDrop(e: DragEvent) {
    e.preventDefault()
  }

  onMaskedFocus() {
    if (!this.maskedInput) return

    const length = this.maskedInput.value.length

    this.maskedInput.setSelectionRange(length, length)
  }
}
